import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SSO Hook - Handles automatic authentication when switching between modules
 * Uses the authToken from localStorage to validate and maintain session
 */
export function useSSO() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      try {
        // Validate token with backend
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update user data in localStorage
        localStorage.setItem('userData', JSON.stringify(response.data));
        
        // Ensure module-specific tokens are set
        localStorage.setItem('financeToken', token);
        localStorage.setItem('hrToken', token);
        localStorage.setItem('salesToken', token);
        localStorage.setItem('docsToken', token);
        
        setIsAuthenticated(true);
      } catch (error) {
        // Token invalid or expired
        console.error('SSO validation failed:', error);
        
        // Clear all auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('financeToken');
        localStorage.removeItem('hrToken');
        localStorage.removeItem('salesToken');
        localStorage.removeItem('docsToken');
        
        setIsAuthenticated(false);
        
        // Redirect to login if on protected route
        const protectedPaths = ['/portal', '/suite', '/admin'];
        if (protectedPaths.some(path => location.pathname.startsWith(path))) {
          navigate('/login', { state: { from: location.pathname } });
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateSession();
  }, [location.pathname, navigate]);

  return { isValidating, isAuthenticated };
}

/**
 * Navigate to a module with SSO
 * Ensures token is synced before navigation
 */
export function useModuleNavigation() {
  const navigate = useNavigate();
  
  const navigateToModule = (moduleId) => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Sync token for the module
    const tokenKey = `${moduleId}Token`;
    localStorage.setItem(tokenKey, token);
    
    // Navigate to module
    navigate(`/suite/${moduleId}`);
  };
  
  return { navigateToModule };
}

/**
 * SSO Protected Route wrapper
 */
export function SSOProtectedRoute({ children }) {
  const { isValidating, isAuthenticated } = useSSO();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isValidating && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isValidating, isAuthenticated, navigate, location.pathname]);

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Validating session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
}

export default useSSO;
